import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Button, Space, Tag, Select, Input, message, Modal, Typography, Dropdown, Menu, Popconfirm, DatePicker, Checkbox, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, MoreOutlined, ClockCircleOutlined, CheckCircleOutlined, DiffOutlined, RobotOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { supplierAPI, orderStatusAPI, productAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Supplier, OrderStatus, SupplierOrder, Product } from '../types.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';
import { generateProductDisplayName } from '../utils/productUtils.ts';
import SupplierOrderForm from '../components/SupplierOrderForm.tsx';
import SupplierOrderStatusModal from '../components/SupplierOrderStatusModal.tsx';
import SupplierOrderDetailsModal from '../components/SupplierOrderDetailsModal.tsx';
import SupplierOrderRectifyModal from '../components/SupplierOrderRectifyModal.tsx';
import SupplierOrderAIImportModal from '../components/SupplierOrderAIImportModal.tsx';

const { Option } = Select;
const { Title } = Typography;
const { RangePicker } = DatePicker;

const SupplierOrders: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('supplier-orders');
  const [rectifyingOrder, setRectifyingOrder] = useState<SupplierOrder | null>(null);
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<OrderStatus[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isAIImportVisible, setIsAIImportVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [editingOrder, setEditingOrder] = useState<SupplierOrder | null>(null);
  const [statusChangingOrder, setStatusChangingOrder] = useState<SupplierOrder | null>(null);
  const [viewingOrder, setViewingOrder] = useState<SupplierOrder | null>(null);
  const [filters, setFilters] = useState({
    order_number: undefined as string | undefined,
    supplier_id: undefined as number | undefined,
    order_status_id: undefined as number | undefined,
    product_id: undefined as number | undefined,
    date_from: undefined as string | undefined,
    date_to: undefined as string | undefined,
    show_completed: false,
  });


  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const apiFilters = {
        order_number: filters.order_number,
        supplier_id: filters.supplier_id,
        order_status_id: filters.order_status_id,
        product_id: filters.product_id,
        date_from: filters.date_from,
        date_to: filters.date_to,
        exclude_completed: !filters.show_completed,
        limit: 0,  // 0 = elenco completo (prima il backend troncava ai 100 ordini più recenti)
      };
      const data = await supplierAPI.getSupplierOrders(apiFilters);
      setOrders(data);
    } catch (error) {
      message.error('Errore nel caricamento degli ordini fornitori');
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadOrders();
  }, [filters, loadOrders]);

  const loadInitialData = async () => {
    try {
      const [suppliersData, orderStatusesData, productsData] = await Promise.all([
        supplierAPI.getSuppliers(),
        orderStatusAPI.getOrderStatuses(),
        productAPI.getProducts()
      ]);
      setSuppliers(suppliersData);
      setOrderStatuses(orderStatusesData);
      setProducts(productsData);
    } catch (error) {
      message.error('Errore nel caricamento dei dati iniziali');
      console.error('Error loading initial data:', error);
    }
  };

  const handleCreate = () => {
    setEditingOrder(null);
    setIsFormModalVisible(true);
  };

  const handleEdit = async (order: SupplierOrder) => {
    // Il form lavora sul dettaglio completo (GET /{id}): il record dell'elenco non portava la
    // destinazione (ordine cliente) di riga e il form rimandava null azzerando le destinazioni
    try {
      const full = await supplierAPI.getSupplierOrder(order.id);
      setEditingOrder(full);
    } catch (error) {
      message.error("Errore nel caricamento dell'ordine");
      return;
    }
    setIsFormModalVisible(true);
  };

  const handleView = (order: SupplierOrder) => {
    setViewingOrder(order);
    setIsDetailsModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await supplierAPI.deleteSupplierOrder(id);
      message.success('Ordine fornitore eliminato con successo');
      loadOrders();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Errore nell\'eliminazione dell\'ordine');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingOrder) {
        await supplierAPI.updateSupplierOrder(editingOrder.id, values);
        message.success('Ordine fornitore aggiornato con successo');
      } else {
        await supplierAPI.createSupplierOrder(values);
        message.success('Ordine fornitore creato con successo');
      }
      setIsFormModalVisible(false);
      loadOrders();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Errore nel salvataggio dell\'ordine');
    }
  };

  const handleStatusChange = (order: SupplierOrder) => {
    setStatusChangingOrder(order);
    setIsStatusModalVisible(true);
  };

  const handleCancelRemainder = async (order: SupplierOrder) => {
    try {
      const result = await supplierAPI.cancelSupplierOrderRemainder(order.id);
      message.success(result?.message || 'Residuo annullato con successo');
      loadOrders();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Errore nell\'annullamento del residuo');
    }
  };

  const handleStatusUpdate = async (statusData: any) => {
    if (!statusChangingOrder) return;
    
    try {
      await supplierAPI.updateSupplierOrderStatus(statusChangingOrder.id, statusData);
      message.success('Stato ordine aggiornato con successo');
      setIsStatusModalVisible(false);
      loadOrders();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Errore nell\'aggiornamento dello stato');
    }
  };




  const handleDateRangeChange = (dates: any, dateStrings: string[]) => {
    setFilters({
      ...filters,
      date_from: dateStrings[0] || undefined,
      date_to: dateStrings[1] || undefined,
    });
  };

  const getStatusColor = (statusName: string) => {
    const colors: { [key: string]: string } = {
      'Nuovo': 'blue',
      'In Attesa': 'gold',
      'In Lavorazione': 'orange',
      'Confermato': 'green',
      'In Produzione': 'cyan',
      'Pronto per Spedizione': 'geekblue',
      'Spedito': 'purple',
      'In Transito': 'orange',
      'Consegnato': 'lime',
      'Annullato': 'red'
    };
    return colors[statusName] || 'default';
  };

  const getActionMenu = (record: SupplierOrder) => {
    // Da "In Produzione" (5) in poi lo stock è caricato in inventario: l'ordine non è
    // eliminabile (guardia backend) e l'annullamento passa da "Annulla Residuo".
    const stockLoaded = [5, 6, 7, 8, 9, 10].includes(record.order_status_id);
    return (
    <Menu>
      <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleView(record)}>
        Visualizza
      </Menu.Item>
      {canEdit && (
        <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
          Modifica
        </Menu.Item>
      )}
      {canEdit && (
        <Menu.Item key="status" icon={<CheckCircleOutlined />} onClick={() => handleStatusChange(record)}>
          Cambia Stato
        </Menu.Item>
      )}
      {canEdit && stockLoaded && (
        <Menu.Item
          key="rectify"
          icon={<DiffOutlined />}
          onClick={() => setRectifyingOrder(record)}
          title="Quantità reale della partita diversa da quella registrata: riallinea produzione/spedizione/bolletta"
        >
          Rettifica Quantità
        </Menu.Item>
      )}
      {canEdit && [5, 6, 7, 8].includes(record.order_status_id) && (
        <Menu.Item key="cancel-remainder" icon={<ClockCircleOutlined />}>
          <Popconfirm
            title="Annullare il residuo mai spedito di questo ordine? La quantità ordinata resta a storico."
            onConfirm={() => handleCancelRemainder(record)}
            okText="Sì"
            cancelText="No"
          >
            Annulla Residuo
          </Popconfirm>
        </Menu.Item>
      )}
      {canEdit && <Menu.Divider />}
      {canEdit && (
        <Menu.Item
          key="delete"
          danger
          icon={<DeleteOutlined />}
          disabled={stockLoaded}
          title={stockLoaded ? 'Stock già caricato in produzione: usare Annulla Residuo' : undefined}
        >
          {stockLoaded ? 'Elimina' : (
            <Popconfirm
              title="Sei sicuro di voler eliminare questo ordine?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sì"
              cancelText="No"
            >
              Elimina
            </Popconfirm>
          )}
        </Menu.Item>
      )}
    </Menu>
    );
  };

  const columns = [
    {
      title: 'Numero Ordine',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (orderNumber: string) => <strong>{orderNumber}</strong>,
    },
    {
      title: 'Fornitore',
      dataIndex: ['supplier', 'name'],
      key: 'supplier',
    },
    {
      title: 'Data Ordine',
      dataIndex: 'order_date',
      key: 'order_date',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Data Consegna Fornitore',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Data Consegna Cliente',
      dataIndex: ['customer_order', 'requested_delivery_date'],
      key: 'customer_delivery_date',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Ordine Cliente',
      key: 'customer_order_info',
      width: 180,
      render: (record: SupplierOrder) => {
        if (!record.customer_order) return '-';
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.customer_order.order_number}</div>
            {record.customer_order.customer_order_number && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                {record.customer_order.customer_order_number}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Stato',
      dataIndex: ['order_status', 'status_name'],
      key: 'status',
      render: (statusName: string) => (
        <Tag color={getStatusColor(statusName)}>
          {statusName}
        </Tag>
      ),
    },
    {
      title: 'Importo Totale',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount: number) => amount ? `€ ${formatCurrency(amount)}` : '-',
    },
    {
      title: 'Scadenza',
      key: 'urgency',
      render: (record: SupplierOrder) => {
        if (record.order_status_id === 10) return <Tag color="green">Evaso</Tag>;
        if (record.order_status_id === 99) return <Tag color="red">Annullato</Tag>;
        if (!record.delivery_date) return '-';
        const today = new Date();
        const deliveryDate = new Date(record.delivery_date);
        const daysUntilDelivery = Math.ceil((deliveryDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
        
        if (daysUntilDelivery < 0) {
          return <Tag color="red" icon={<ClockCircleOutlined />}>In Ritardo</Tag>;
        } else if (daysUntilDelivery <= 3) {
          return <Tag color="orange" icon={<ClockCircleOutlined />}>Urgente</Tag>;
        } else if (daysUntilDelivery <= 7) {
          return <Tag color="yellow">Prossimo</Tag>;
        }
        return <Tag color="green">Normale</Tag>;
      },
    },    {
      title: 'Azioni',
      key: 'actions',
      render: (_: any, record: SupplierOrder) => (
        <Dropdown overlay={getActionMenu(record)} trigger={['click']}>
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>Gestione Ordini Fornitori</Title>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="Filtra per Numero Ordine"
              style={{ width: 200 }}
              value={filters.order_number}
              onChange={(e) => setFilters({ ...filters, order_number: e.target.value || undefined })}
              allowClear
            />

            <Select
              placeholder="Filtra per Fornitore"
              style={{ width: 200 }}
              value={filters.supplier_id}
              onChange={(value) => setFilters({ ...filters, supplier_id: value })}
              allowClear
            >
              {suppliers.map((supplier) => (
                <Option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </Option>
              ))}
            </Select>
            
            <Select
              placeholder="Filtra per Stato"
              style={{ width: 150 }}
              value={filters.order_status_id}
              onChange={(value) => setFilters({ ...filters, order_status_id: value })}
              allowClear
            >
              {orderStatuses.map((status) => (
                <Option key={status.id} value={status.id}>
                  {status.status_name}
                </Option>
              ))}
            </Select>
            
            <Select
              placeholder="Filtra per Prodotto"
              style={{ width: 280 }}
              value={filters.product_id}
              onChange={(value) => setFilters({ ...filters, product_id: value })}
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {products.map((product) => (
                <Option key={product.id} value={product.id}>
                  {generateProductDisplayName(product.internal_product_code, product.size, product.color, product.product_description)}
                </Option>
              ))}
            </Select>

            <RangePicker
              format="DD/MM/YYYY"
              placeholder={['Data Da', 'Data A']}
              onChange={handleDateRangeChange}
              value={[
                filters.date_from ? dayjs(filters.date_from) : null,
                filters.date_to ? dayjs(filters.date_to) : null
              ]}
            />
            
            <Checkbox
              checked={filters.show_completed}
              onChange={(e) => setFilters({ ...filters, show_completed: e.target.checked })}
            >
              Visualizza Ordini Evasi/Annullati:
            </Checkbox>
            
            {canEdit && (
              <Tooltip title="Carica una o più Proforma Invoice (fatture proforma - PI) del fornitore tramite AI">
                <Button
                  icon={<RobotOutlined />}
                  onClick={() => setIsAIImportVisible(true)}
                >
                  Importa con AI
                </Button>
              </Tooltip>
            )}
            {canEdit && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Nuovo Ordine
              </Button>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            total: orders.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: true }}
        />

        {/* Modal Form Ordine */}
        <SupplierOrderAIImportModal
          visible={isAIImportVisible}
          onClose={() => setIsAIImportVisible(false)}
          onSuccess={loadOrders}
        />

        <Modal
          title={editingOrder ? 'Modifica Ordine Fornitore' : 'Nuovo Ordine Fornitore'}
          open={isFormModalVisible}
          onCancel={() => setIsFormModalVisible(false)}
          footer={null}
          width="90%"
          destroyOnClose
        >
          <SupplierOrderForm
            initialValues={editingOrder}
            suppliers={suppliers}
            orderStatuses={orderStatuses}
            onSubmit={handleSubmit}
            onCancel={() => setIsFormModalVisible(false)}
          />
        </Modal>

        {/* Modal per cambio stato */}
        <Modal
          title="Cambia Stato Ordine"
          open={isStatusModalVisible}
          onCancel={() => setIsStatusModalVisible(false)}
          footer={null}
          destroyOnClose
        >
          <SupplierOrderStatusModal
            order={statusChangingOrder}
            orderStatuses={orderStatuses}
            onSubmit={handleStatusUpdate}
            onCancel={() => setIsStatusModalVisible(false)}
          />
        </Modal>

        {/* Modal per dettagli ordine */}
        <Modal
          title="Dettagli Ordine Fornitore"
          open={isDetailsModalVisible}
          onCancel={() => setIsDetailsModalVisible(false)}
          footer={null}
          width={1000}
          destroyOnClose
        >
          <SupplierOrderDetailsModal
            order={viewingOrder}
            onClose={() => setIsDetailsModalVisible(false)}
          />
        </Modal>

        {/* Rettifica Quantità della partita */}
        <SupplierOrderRectifyModal
          visible={!!rectifyingOrder}
          order={rectifyingOrder}
          onCancel={() => setRectifyingOrder(null)}
          onSuccess={() => {
            setRectifyingOrder(null);
            loadOrders();
          }}
        />

      </Card>
    </div>
  );
};

export default SupplierOrders;